import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import history from 'history.js';

import { getNextItemByKey, isObjectEmpty } from 'utils/common';
import { submitTackingVideoInfo } from 'reduxModules/tracking/action';
import {
  COURSE,
  ENDED,
  FAST_FORWARD_END,
  FAST_FORWARD_START,
  PAUSE,
  PLAY,
  PROGRESS_DRAG_END,
  PROGRESS_DRAG_START,
  REWIND_END,
  REWIND_START,
  STOP,
} from 'constants/tracingEvent';
import { FORWARD } from 'components/BrightcovePlayer/constants';
import { getTimezone } from 'utils/time';

const getEpisodeEnrolled = (type, lesson) => {
  if (type === COURSE) return lesson.userPurchased;

  return lesson.state === 'video_player' && !lesson.isFree;
};

const getDurationTime = lesson => {
  return lesson.videoDuration ?? lesson.durationTime;
};

const compareCurrentTimeToEnd = (currentTime, endTime) => {
  const res = Math.abs(currentTime - endTime);

  if (res > 1) return false;

  return true;
};

const usePlaylist = ({
  type,
  playerRef,
  id: contentId,
  initialLessonId,
  lessonPlaylist,
  getWatchable,
  onlyControlByUrl,
  onPlayCurrentLesson,
  noAutoPlay,
  goLessonPage,
  onPlayNextLesson,
  trailerLesson,
}) => {
  const hasInitialLessonId = !Number.isNaN(initialLessonId);
  const initialCurrentLesson = useMemo(
    () =>
      hasInitialLessonId
        ? lessonPlaylist.find(({ id }) => id === initialLessonId) || {}
        : trailerLesson || {},
    [hasInitialLessonId, initialLessonId, lessonPlaylist, trailerLesson],
  );

  const dispatchTrackingEventActionRef = useRef();
  const [currentLesson, setCurrentLesson] = useState(initialCurrentLesson);
  const [showAutoPlayController, setShowAutoPlayController] = useState(false);
  const dispatch = useDispatch();

  const getIsCurrent = useCallback(lesson => currentLesson.id === lesson.id, [
    currentLesson.id,
  ]);

  const setLesson = useCallback(
    lessonInfo => {
      if (onlyControlByUrl) {
        goLessonPage(lessonInfo.id);
        return;
      }
      setCurrentLesson(lessonInfo);
    },
    [onlyControlByUrl, goLessonPage],
  );

  const submitTackingCreator = targetLesson => ({
    event,
    progressDuration,
  }) => {
    if (isObjectEmpty(targetLesson) || targetLesson.isTrailer) return;

    dispatch(
      submitTackingVideoInfo({
        event,
        progressDuration: Math.round(progressDuration),
        targetType: type,
        targetId: contentId,
        episodeId: targetLesson.id,
        isEpisodeEnrolled: getEpisodeEnrolled(type, targetLesson),
        isBackground: document.hidden,
        isAutoPlay: !noAutoPlay,
        timezone: getTimezone(),
      }),
    );
  };

  const nextLesson = useMemo(
    () => getNextItemByKey(lessonPlaylist, currentLesson, 'id'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentId, lessonPlaylist, currentLesson.id],
  );

  const isLastLesson = useMemo(() => currentLesson?.id === nextLesson?.id, [
    currentLesson?.id,
    nextLesson?.id,
  ]);

  const playCurrentLesson = useCallback(
    ({ currentTime }) => {
      setCurrentLesson(state => ({
        ...state,
        isPlaying: true,
      }));
      onPlayCurrentLesson(currentLesson);

      dispatchTrackingEventActionRef.current({
        event: PLAY,
        progressDuration: currentTime,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onPlayCurrentLesson, currentLesson.id],
  );

  const pauseCurrentLesson = useCallback(
    ({ currentTime }) => {
      setCurrentLesson(state => ({
        ...state,
        isPlaying: false,
      }));

      const currentDurationTime = getDurationTime(currentLesson);

      if (compareCurrentTimeToEnd(currentTime, currentDurationTime)) return;

      dispatchTrackingEventActionRef.current({
        event: PAUSE,
        progressDuration: currentTime,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLesson.id],
  );

  const playNextLesson = useCallback(() => {
    if (getIsCurrent(nextLesson) || noAutoPlay) return;

    setLesson(nextLesson);
    setShowAutoPlayController(false);

    if (typeof onPlayNextLesson === 'function') {
      onPlayNextLesson(nextLesson);
    }
  }, [getIsCurrent, nextLesson, noAutoPlay, onPlayNextLesson, setLesson]);

  const onProgressDragStart = useCallback(
    ({ currentTime }) => {
      dispatchTrackingEventActionRef.current({
        event: PROGRESS_DRAG_START,
        progressDuration: currentTime,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLesson.id],
  );

  const onProgressDragEnd = useCallback(
    ({ currentTime }) => {
      dispatchTrackingEventActionRef.current({
        event: PROGRESS_DRAG_END,
        progressDuration: currentTime,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLesson.id],
  );

  const onForwardStart = useCallback(
    ({ currentTime }) => {
      dispatchTrackingEventActionRef.current({
        event: FAST_FORWARD_START,
        progressDuration: currentTime,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLesson.id],
  );

  const onRewindStart = useCallback(
    ({ currentTime }) => {
      dispatchTrackingEventActionRef.current({
        event: REWIND_START,
        progressDuration: currentTime,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLesson.id],
  );

  const onSeekEnd = useCallback(
    ({ seekType, currentTime }) => {
      if (!seekType) return;

      const event = seekType === FORWARD ? FAST_FORWARD_END : REWIND_END;

      dispatchTrackingEventActionRef.current({
        event,
        progressDuration: currentTime,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLesson.id],
  );

  const onEndedLesson = useCallback(
    ({ currentTime, isHideNextVideoController = false }) => {
      dispatchTrackingEventActionRef.current({
        event: ENDED,
        progressDuration: currentTime,
      });

      if (isLastLesson || isHideNextVideoController) return;
      playerRef.current.handlePause();
      setShowAutoPlayController(true);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLastLesson, playerRef, currentLesson.id],
  );

  useEffect(() => {
    if (onlyControlByUrl || currentLesson.id || !nextLesson) {
      return;
    }

    setCurrentLesson(nextLesson);
  }, [onlyControlByUrl, currentLesson.id, nextLesson]);

  useEffect(() => {
    if (
      (!onlyControlByUrl && currentLesson.id) ||
      getIsCurrent(initialCurrentLesson)
    ) {
      return;
    }

    setCurrentLesson(initialCurrentLesson);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contentId,
    hasInitialLessonId,
    getIsCurrent,
    initialCurrentLesson.id,
    initialCurrentLesson.videoId,
  ]);

  useEffect(() => {
    if (
      !onlyControlByUrl ||
      (hasInitialLessonId && currentLesson.videoId !== undefined)
    ) {
      return;
    }

    if (typeof onPlayNextLesson === 'function') {
      onPlayNextLesson(nextLesson);
    }
  }, [
    contentId,
    onlyControlByUrl,
    hasInitialLessonId,
    currentLesson.videoId,
    goLessonPage,
    onPlayNextLesson,
    nextLesson,
  ]);

  useEffect(() => {
    if (!getWatchable(currentLesson)) {
      return;
    }

    setCurrentLesson(state => ({
      ...state,
      videoIsReady: true,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLesson.id, getWatchable]);

  useEffect(() => {
    if (currentLesson.isPlaying && showAutoPlayController) {
      setShowAutoPlayController(false);
    }
  }, [currentLesson.isPlaying, showAutoPlayController]);

  useEffect(() => {
    dispatchTrackingEventActionRef.current = submitTackingCreator(
      currentLesson,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLesson.id]);

  useEffect(() => {
    const unListen = history.listen(() => {
      if (dispatchTrackingEventActionRef.current) {
        dispatchTrackingEventActionRef.current({
          event: STOP,
          progressDuration: 0,
        });
      }
    });

    return () => {
      unListen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    playerRef,
    currentLesson,
    nextLesson,
    isLastLesson,
    playCurrentLesson,
    pauseCurrentLesson,
    playNextLesson,
    onEndedLesson,
    showAutoPlayController,
    setCurrentLesson,
    getIsCurrent,
    onProgressDragStart,
    onProgressDragEnd,
    onForwardStart,
    onRewindStart,
    onSeekEnd,
  };
};

export default usePlaylist;
